import React, { useState, useMemo } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  Typography,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  Chip,
  Alert,
  AlertTitle,
  TextField,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import CategoryOutlinedIcon from '@mui/icons-material/CategoryOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import ScaleOutlinedIcon from '@mui/icons-material/ScaleOutlined';
import SearchIcon from '@mui/icons-material/Search';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import ListAltOutlinedIcon from '@mui/icons-material/ListAltOutlined';
import GridViewOutlinedIcon from '@mui/icons-material/GridViewOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import DownloadIcon from '@mui/icons-material/Download';
import { exportRateSheetToExcel } from './exportRateSheetExcel';

export interface ZoneMapping {
  vendorCountry?: string;
  country?: string;
  countryCode?: string;
  zone: string | number;
  transitDays?: number | string | null;
}

export interface RateSlab {
  weightKg: number;
  rates: Record<string, number>;
}

export interface RateBracket {
  weightFrom: number;
  weightTo: number;
  perKg: Record<string, number>;
}

export interface RateStructure {
  slabs?: RateSlab[];
  brackets?: RateBracket[];
}

export interface WarningItem {
  code?: string;
  message: string;
}

export interface OtherChargeItem {
  name?: string;
  charge?: number | string;
  unit?: string;
  [key: string]: unknown;
}

export interface ConvertApiResponse {
  vendor?: string;
  rateType?: string;
  sourceS3Key?: string;
  zones?: ZoneMapping[];
  rates?: RateStructure;
  otherCharges?: OtherChargeItem[];
  warnings?: WarningItem[];
  msg?: string;
  data?: ConvertApiResponse;
  [key: string]: unknown;
}

interface RateSheetPreviewProps {
  data: ConvertApiResponse;
}

function formatCurrency(amount: number | string | undefined | null): string {
  if (amount === undefined || amount === null) return '—';
  const num = typeof amount === 'number' ? amount : parseFloat(String(amount));
  if (isNaN(num)) return String(amount);
  return num.toLocaleString('en-IN', { maximumFractionDigits: 2 });
}

export const RateSheetPreview: React.FC<RateSheetPreviewProps> = ({ data }) => {
  const theme = useTheme();

  // Normalize data payload (handle possible nested data.data)
  const normalizedData: ConvertApiResponse = data?.data && typeof data.data === 'object' ? data.data : data;

  const {
    vendor = 'N/A',
    rateType = 'N/A',
    sourceS3Key = '',
    zones = [],
    rates,
    warnings = [],
    otherCharges = [],
  } = normalizedData;

  const slabs = rates?.slabs || [];
  const brackets = rates?.brackets || [];

  // Active Tab: 0 = Rates Matrix, 1 = Country & Zone Directory, 2 = Zone Overview, 3 = Additional Charges
  const [activeTab, setActiveTab] = useState<number>(0);

  // Rate table sub-view: 'slabs' | 'brackets'
  const [rateTableView, setRateTableView] = useState<'slabs' | 'brackets'>('slabs');

  // Country search and zone filter
  const [countrySearch, setCountrySearch] = useState<string>('');
  const [selectedZoneFilter, setSelectedZoneFilter] = useState<string>('all');

  // Pagination states
  const [slabPage, setSlabPage] = useState<number>(0);
  const [slabRowsPerPage, setSlabRowsPerPage] = useState<number>(10);

  const [countryPage, setCountryPage] = useState<number>(0);
  const [countryRowsPerPage, setCountryRowsPerPage] = useState<number>(10);

  // Warning section expandable state
  const [isWarningsExpanded, setIsWarningsExpanded] = useState<boolean>(false);

  // Extract all unique zones from slabs, brackets, and zones list
  const uniqueZoneList = useMemo(() => {
    const zoneSet = new Set<string>();

    zones.forEach(z => {
      if (z.zone !== undefined && z.zone !== null) zoneSet.add(String(z.zone));
    });

    slabs.forEach(s => {
      if (s.rates) Object.keys(s.rates).forEach(z => zoneSet.add(z));
    });

    brackets.forEach(b => {
      if (b.perKg) Object.keys(b.perKg).forEach(z => zoneSet.add(z));
    });

    return Array.from(zoneSet).sort((a, b) => {
      const numA = Number(a);
      const numB = Number(b);
      if (!isNaN(numA) && !isNaN(numB)) return numA - numB;
      return a.localeCompare(b);
    });
  }, [zones, slabs, brackets]);

  // Group countries by zone
  const countriesByZone = useMemo(() => {
    const map = new Map<string, ZoneMapping[]>();
    zones.forEach(item => {
      const z = String(item.zone ?? 'Unassigned');
      if (!map.has(z)) map.set(z, []);
      map.get(z)?.push(item);
    });
    return map;
  }, [zones]);

  // Filtered countries for the Country & Zone Directory table
  const filteredCountries = useMemo(() => {
    return zones.filter(item => {
      const matchesSearch =
        !countrySearch.trim() ||
        (item.country && item.country.toLowerCase().includes(countrySearch.toLowerCase())) ||
        (item.countryCode && item.countryCode.toLowerCase().includes(countrySearch.toLowerCase())) ||
        (item.vendorCountry && item.vendorCountry.toLowerCase().includes(countrySearch.toLowerCase())) ||
        String(item.zone).toLowerCase().includes(countrySearch.toLowerCase());

      const matchesZone = selectedZoneFilter === 'all' || String(item.zone) === selectedZoneFilter;

      return matchesSearch && matchesZone;
    });
  }, [zones, countrySearch, selectedZoneFilter]);

  // Derive weight range label
  const weightRangeLabel = useMemo(() => {
    if (!slabs.length && !brackets.length) return '—';
    const minWeight = slabs.length ? slabs[0].weightKg : brackets[0]?.weightFrom;
    const maxWeight = brackets.length
      ? brackets[brackets.length - 1].weightTo > 50000
        ? `${brackets[brackets.length - 1].weightFrom} kg+`
        : `${brackets[brackets.length - 1].weightTo} kg`
      : slabs[slabs.length - 1]?.weightKg
      ? `${slabs[slabs.length - 1].weightKg} kg`
      : '—';
    return `${minWeight} kg – ${maxWeight}`;
  }, [slabs, brackets]);

  // Clean filename for display
  const displayFileName = sourceS3Key ? sourceS3Key.split('/').pop() : '';

  return (
    <Box sx={{ mt: 3, width: '100%' }}>
      {/* ========================================================================= */}
      {/* 1. Header & Success Status Banner                                        */}
      {/* ========================================================================= */}
      <Card
        variant="outlined"
        sx={{
          mb: 3,
          borderRadius: 2,
          borderColor: alpha(theme.palette.success.main, 0.4),
          backgroundColor: alpha(theme.palette.success.main, 0.03),
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: 2.5 }}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1.5 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <CheckCircleIcon sx={{ color: theme.palette.success.main, fontSize: 32 }} />
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.text.primary, lineHeight: 1.2 }}>
                  Rate Sheet Converted Successfully
                </Typography>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary, mt: 0.5 }}>
                  Structured rates and zone mappings have been parsed from{' '}
                  <strong style={{ color: theme.palette.text.primary }}>
                    {displayFileName || 'the uploaded file'}
                  </strong>
                  .
                </Typography>
              </Box>
            </Box>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon />}
              onClick={() => exportRateSheetToExcel(normalizedData)}
              sx={{
                fontWeight: 600,
                textTransform: 'none',
                borderColor: alpha(theme.palette.success.main, 0.5),
                color: theme.palette.success.dark || theme.palette.success.main,
                '&:hover': {
                  borderColor: theme.palette.success.main,
                  backgroundColor: alpha(theme.palette.success.main, 0.08),
                },
              }}
            >
              Download Excel
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* ========================================================================= */}
      {/* 2. Top Summary KPI Cards                                                  */}
      {/* ========================================================================= */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* Vendor */}
        <Grid item xs={12} sm={6} md={3}>
          <Card
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography
                variant="caption"
                sx={{ color: theme.palette.text.secondary, fontWeight: 600, textTransform: 'uppercase' }}
              >
                Carrier / Service
              </Typography>
              <LocalShippingOutlinedIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mt: 1, textTransform: 'capitalize' }}>
              {vendor || '—'}
            </Typography>
          </Card>
        </Grid>

        {/* Rate Type */}
        <Grid item xs={12} sm={6} md={2}>
          <Card
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography
                variant="caption"
                sx={{ color: theme.palette.text.secondary, fontWeight: 600, textTransform: 'uppercase' }}
              >
                Rate Type
              </Typography>
              <CategoryOutlinedIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mt: 1, textTransform: 'capitalize' }}>
              {rateType || '—'}
            </Typography>
          </Card>
        </Grid>

        {/* Zones Covered */}
        <Grid item xs={12} sm={4} md={2}>
          <Card
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography
                variant="caption"
                sx={{ color: theme.palette.text.secondary, fontWeight: 600, textTransform: 'uppercase' }}
              >
                Total Zones
              </Typography>
              <PublicOutlinedIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mt: 1 }}>
              {uniqueZoneList.length} Zones
            </Typography>
          </Card>
        </Grid>

        {/* Countries Mapped */}
        <Grid item xs={12} sm={4} md={2.5}>
          <Card
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography
                variant="caption"
                sx={{ color: theme.palette.text.secondary, fontWeight: 600, textTransform: 'uppercase' }}
              >
                Countries Mapped
              </Typography>
              <PlaceOutlinedIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, mt: 1 }}>
              {zones.length} Destinations
            </Typography>
          </Card>
        </Grid>

        {/* Weight Range */}
        <Grid item xs={12} sm={4} md={2.5}>
          <Card
            variant="outlined"
            sx={{
              p: 2,
              borderRadius: 2,
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              backgroundColor: theme.palette.background.paper,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography
                variant="caption"
                sx={{ color: theme.palette.text.secondary, fontWeight: 600, textTransform: 'uppercase' }}
              >
                Weight Range
              </Typography>
              <ScaleOutlinedIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
            </Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, mt: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
            >
              {weightRangeLabel}
            </Typography>
          </Card>
        </Grid>
      </Grid>

      {/* ========================================================================= */}
      {/* 3. Tab Navigation & Tables                                                */}
      {/* ========================================================================= */}
      <Card variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden', mb: 3 }}>
        <Box
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            px: 2,
            pt: 1,
            backgroundColor: alpha(theme.palette.primary.main, 0.02),
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(_e, newValue) => setActiveTab(newValue)}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab
              icon={<TableChartOutlinedIcon sx={{ fontSize: 18, mr: 0.5 }} />}
              iconPosition="start"
              label={`Rates Matrix (${slabs.length} Slabs / ${brackets.length} Brackets)`}
              sx={{ fontWeight: 600, textTransform: 'none', minHeight: 48 }}
            />
            <Tab
              icon={<ListAltOutlinedIcon sx={{ fontSize: 18, mr: 0.5 }} />}
              iconPosition="start"
              label={`Country Directory (${zones.length})`}
              sx={{ fontWeight: 600, textTransform: 'none', minHeight: 48 }}
            />
            <Tab
              icon={<GridViewOutlinedIcon sx={{ fontSize: 18, mr: 0.5 }} />}
              iconPosition="start"
              label={`Zones Summary (${uniqueZoneList.length})`}
              sx={{ fontWeight: 600, textTransform: 'none', minHeight: 48 }}
            />
            {otherCharges && otherCharges.length > 0 && (
              <Tab
                label={`Other Charges (${otherCharges.length})`}
                sx={{ fontWeight: 600, textTransform: 'none', minHeight: 48 }}
              />
            )}
          </Tabs>
        </Box>

        {/* ===================================================================== */}
        {/* TAB 0: Rates Matrix (Slabs & Brackets)                                */}
        {/* ===================================================================== */}
        {activeTab === 0 && (
          <Box sx={{ p: 2.5 }}>
            {/* View switcher (Weight Slabs vs Heavy Cargo Brackets) */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 2,
                mb: 2,
              }}
            >
              <FormControl component="fieldset">
                <RadioGroup
                  row
                  value={rateTableView}
                  onChange={e => {
                    setRateTableView(e.target.value as 'slabs' | 'brackets');
                    setSlabPage(0);
                  }}
                >
                  <FormControlLabel
                    value="slabs"
                    control={<Radio size="small" />}
                    label={
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Weight Slabs (0.5 kg – 30 kg)
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    value="brackets"
                    control={<Radio size="small" />}
                    label={
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        Per-Kg Rate Brackets (30.1 kg+)
                      </Typography>
                    }
                  />
                </RadioGroup>
              </FormControl>

              <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                Showing rate values per slab / bracket across all <strong>{uniqueZoneList.length} Zones</strong>
              </Typography>
            </Box>

            {/* View A: Weight Slabs Table */}
            {rateTableView === 'slabs' && (
              <>
                {slabs.length === 0 ? (
                  <Alert severity="info" sx={{ my: 2 }}>
                    No weight slab data available in this rate sheet.
                  </Alert>
                ) : (
                  <>
                    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5, maxHeight: 500 }}>
                      <Table stickyHeader size="small" aria-label="rate slabs table">
                        <TableHead>
                          <TableRow>
                            <TableCell
                              sx={{
                                fontWeight: 700,
                                backgroundColor: theme.palette.primary.main,
                                color: '#ffffff',
                                whiteSpace: 'nowrap',
                                minWidth: 100,
                              }}
                            >
                              Weight
                            </TableCell>
                            {uniqueZoneList.map(zoneName => (
                              <TableCell
                                key={zoneName}
                                align="right"
                                sx={{
                                  fontWeight: 700,
                                  backgroundColor: theme.palette.primary.main,
                                  color: '#ffffff',
                                  whiteSpace: 'nowrap',
                                  minWidth: 80,
                                }}
                              >
                                Zone {zoneName}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {slabs
                            .slice(slabPage * slabRowsPerPage, slabPage * slabRowsPerPage + slabRowsPerPage)
                            .map((slabItem, idx) => (
                              <TableRow
                                key={idx}
                                hover
                                sx={{
                                  '&:nth-of-type(even)': {
                                    backgroundColor: alpha(theme.palette.primary.main, 0.02),
                                  },
                                }}
                              >
                                <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                                  {slabItem.weightKg} kg
                                </TableCell>
                                {uniqueZoneList.map(zoneName => {
                                  const rateVal = slabItem.rates?.[zoneName];
                                  return (
                                    <TableCell key={zoneName} align="right" sx={{ whiteSpace: 'nowrap' }}>
                                      {formatCurrency(rateVal)}
                                    </TableCell>
                                  );
                                })}
                              </TableRow>
                            ))}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    <TablePagination
                      rowsPerPageOptions={[10, 20, 30, 50]}
                      component="div"
                      count={slabs.length}
                      rowsPerPage={slabRowsPerPage}
                      page={slabPage}
                      onPageChange={(_e, newPage) => setSlabPage(newPage)}
                      onRowsPerPageChange={e => {
                        setSlabRowsPerPage(parseInt(e.target.value, 10));
                        setSlabPage(0);
                      }}
                    />
                  </>
                )}
              </>
            )}

            {/* View B: Per-Kg Brackets Table */}
            {rateTableView === 'brackets' && (
              <>
                {brackets.length === 0 ? (
                  <Alert severity="info" sx={{ my: 2 }}>
                    No per-kg bracket data available in this rate sheet.
                  </Alert>
                ) : (
                  <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5, maxHeight: 500 }}>
                    <Table stickyHeader size="small" aria-label="rate brackets table">
                      <TableHead>
                        <TableRow>
                          <TableCell
                            sx={{
                              fontWeight: 700,
                              backgroundColor: theme.palette.primary.main,
                              color: '#ffffff',
                              whiteSpace: 'nowrap',
                              minWidth: 150,
                            }}
                          >
                            Weight Bracket
                          </TableCell>
                          {uniqueZoneList.map(zoneName => (
                            <TableCell
                              key={zoneName}
                              align="right"
                              sx={{
                                fontWeight: 700,
                                backgroundColor: theme.palette.primary.main,
                                color: '#ffffff',
                                whiteSpace: 'nowrap',
                                minWidth: 80,
                              }}
                            >
                              Zone {zoneName} (/kg)
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {brackets.map((bracketItem, idx) => {
                          const rangeLabel =
                            bracketItem.weightTo > 50000
                              ? `${bracketItem.weightFrom} kg +`
                              : `${bracketItem.weightFrom} kg – ${bracketItem.weightTo} kg`;

                          return (
                            <TableRow
                              key={idx}
                              hover
                              sx={{
                                '&:nth-of-type(even)': {
                                  backgroundColor: alpha(theme.palette.primary.main, 0.02),
                                },
                              }}
                            >
                              <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{rangeLabel}</TableCell>
                              {uniqueZoneList.map(zoneName => {
                                const perKgRate = bracketItem.perKg?.[zoneName];
                                return (
                                  <TableCell key={zoneName} align="right" sx={{ whiteSpace: 'nowrap' }}>
                                    {formatCurrency(perKgRate)}
                                  </TableCell>
                                );
                              })}
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </>
            )}
          </Box>
        )}

        {/* ===================================================================== */}
        {/* TAB 1: Country & Zone Directory                                       */}
        {/* ===================================================================== */}
        {activeTab === 1 && (
          <Box sx={{ p: 2.5 }}>
            {/* Filter & Search Toolbar */}
            <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
              <Grid item xs={12} sm={8} md={6}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Search by country name, code, or zone..."
                  value={countrySearch}
                  onChange={e => {
                    setCountrySearch(e.target.value);
                    setCountryPage(0);
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: theme.palette.text.secondary }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={4} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel id="zone-filter-label">Filter Zone</InputLabel>
                  <Select
                    labelId="zone-filter-label"
                    label="Filter Zone"
                    value={selectedZoneFilter}
                    onChange={e => {
                      setSelectedZoneFilter(e.target.value);
                      setCountryPage(0);
                    }}
                  >
                    <MenuItem value="all">All Zones ({zones.length})</MenuItem>
                    {uniqueZoneList.map(z => (
                      <MenuItem key={z} value={z}>
                        Zone {z} ({countriesByZone.get(z)?.length || 0})
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={3} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                  Showing <strong>{filteredCountries.length}</strong> of <strong>{zones.length}</strong> destinations
                </Typography>
              </Grid>
            </Grid>

            {/* Countries Table */}
            {filteredCountries.length === 0 ? (
              <Alert severity="info" sx={{ my: 2 }}>
                No destination countries found matching your search.
              </Alert>
            ) : (
              <>
                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5, maxHeight: 500 }}>
                  <Table stickyHeader size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell
                          sx={{ fontWeight: 700, backgroundColor: theme.palette.primary.main, color: '#ffffff' }}
                        >
                          Standard Country Name
                        </TableCell>
                        <TableCell
                          sx={{ fontWeight: 700, backgroundColor: theme.palette.primary.main, color: '#ffffff' }}
                        >
                          ISO Code
                        </TableCell>
                        <TableCell
                          sx={{ fontWeight: 700, backgroundColor: theme.palette.primary.main, color: '#ffffff' }}
                        >
                          Vendor Label in Sheet
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{ fontWeight: 700, backgroundColor: theme.palette.primary.main, color: '#ffffff' }}
                        >
                          Assigned Zone
                        </TableCell>
                        <TableCell
                          align="center"
                          sx={{ fontWeight: 700, backgroundColor: theme.palette.primary.main, color: '#ffffff' }}
                        >
                          Transit Days
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredCountries
                        .slice(countryPage * countryRowsPerPage, countryPage * countryRowsPerPage + countryRowsPerPage)
                        .map((cItem, idx) => (
                          <TableRow
                            key={idx}
                            hover
                            sx={{
                              '&:nth-of-type(even)': {
                                backgroundColor: alpha(theme.palette.primary.main, 0.02),
                              },
                            }}
                          >
                            <TableCell sx={{ fontWeight: 600 }}>{cItem.country || '—'}</TableCell>
                            <TableCell>
                              {cItem.countryCode ? (
                                <Chip
                                  size="small"
                                  label={cItem.countryCode}
                                  sx={{
                                    fontWeight: 700,
                                    fontSize: '0.75rem',
                                    height: 22,
                                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                    color: theme.palette.primary.main,
                                  }}
                                />
                              ) : (
                                '—'
                              )}
                            </TableCell>
                            <TableCell sx={{ color: theme.palette.text.secondary }}>
                              {cItem.vendorCountry || '—'}
                            </TableCell>
                            <TableCell align="center">
                              <Chip
                                size="small"
                                label={`Zone ${cItem.zone}`}
                                sx={{
                                  fontWeight: 600,
                                  fontSize: '0.75rem',
                                  height: 22,
                                  backgroundColor: alpha(
                                    theme.palette.secondary?.main || theme.palette.primary.main,
                                    0.12,
                                  ),
                                  color: theme.palette.secondary?.main || theme.palette.primary.main,
                                }}
                              />
                            </TableCell>
                            <TableCell align="center" sx={{ color: theme.palette.text.secondary }}>
                              {cItem.transitDays ?? '—'}
                            </TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <TablePagination
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  component="div"
                  count={filteredCountries.length}
                  rowsPerPage={countryRowsPerPage}
                  page={countryPage}
                  onPageChange={(_e, newPage) => setCountryPage(newPage)}
                  onRowsPerPageChange={e => {
                    setCountryRowsPerPage(parseInt(e.target.value, 10));
                    setCountryPage(0);
                  }}
                />
              </>
            )}
          </Box>
        )}

        {/* ===================================================================== */}
        {/* TAB 2: Zones Overview Grid                                            */}
        {/* ===================================================================== */}
        {activeTab === 2 && (
          <Box sx={{ p: 2.5 }}>
            <Grid container spacing={2}>
              {uniqueZoneList.map(zoneKey => {
                const zoneCountries = countriesByZone.get(zoneKey) || [];

                return (
                  <Grid item xs={12} sm={6} md={4} key={zoneKey}>
                    <Card
                      variant="outlined"
                      sx={{
                        p: 2,
                        borderRadius: 2,
                        height: '100%',
                        backgroundColor: alpha(theme.palette.primary.main, 0.02),
                        display: 'flex',
                        flexDirection: 'column',
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                          Zone {zoneKey}
                        </Typography>
                        <Chip
                          size="small"
                          label={`${zoneCountries.length} Countries`}
                          sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                        />
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: 0.5,
                          maxHeight: 160,
                          overflowY: 'auto',
                          pr: 0.5,
                        }}
                      >
                        {zoneCountries.length === 0 ? (
                          <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>
                            No countries mapped
                          </Typography>
                        ) : (
                          zoneCountries.map((c, cIdx) => (
                            <Chip
                              key={cIdx}
                              size="small"
                              variant="outlined"
                              label={c.country || c.vendorCountry || c.countryCode}
                              sx={{ fontSize: '0.75rem', height: 22 }}
                            />
                          ))
                        )}
                      </Box>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        )}

        {/* ===================================================================== */}
        {/* TAB 3: Other Charges / Surcharges (if present)                         */}
        {/* ===================================================================== */}
        {activeTab === 3 && (
          <Box sx={{ p: 2.5 }}>
            {otherCharges.length === 0 ? (
              <Alert severity="info">No additional surcharges or fees specified.</Alert>
            ) : (
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell
                        sx={{ fontWeight: 700, backgroundColor: theme.palette.primary.main, color: '#ffffff' }}
                      >
                        Charge Name
                      </TableCell>
                      <TableCell
                        align="right"
                        sx={{ fontWeight: 700, backgroundColor: theme.palette.primary.main, color: '#ffffff' }}
                      >
                        Rate / Fee
                      </TableCell>
                      <TableCell
                        sx={{ fontWeight: 700, backgroundColor: theme.palette.primary.main, color: '#ffffff' }}
                      >
                        Unit / Notes
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {otherCharges.map((charge, idx) => (
                      <TableRow key={idx} hover>
                        <TableCell sx={{ fontWeight: 600 }}>{charge.name || `Charge #${idx + 1}`}</TableCell>
                        <TableCell align="right">{formatCurrency(charge.charge)}</TableCell>
                        <TableCell sx={{ color: theme.palette.text.secondary }}>{charge.unit || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        )}
      </Card>

      {/* ========================================================================= */}
      {/* 4. Warnings Alert (Positioned below the tables with Read More toggle)     */}
      {/* ========================================================================= */}
      {warnings && warnings.length > 0 && (
        <Alert
          severity="warning"
          icon={<WarningAmberOutlinedIcon />}
          sx={{
            mt: 2,
            borderRadius: 2,
            border: `1px solid ${alpha(theme.palette.warning?.main || '#ed6c02', 0.3)}`,
            backgroundColor: alpha(theme.palette.warning?.main || '#ed6c02', 0.05),
          }}
        >
          <AlertTitle sx={{ fontWeight: 700, color: theme.palette.warning?.dark || '#c75100' }}>
            Warning {warnings.length > 1 ? `(${warnings.length})` : ''}
          </AlertTitle>
          <Box component="ul" sx={{ pl: 2, mb: 0, mt: 0.5 }}>
            {(isWarningsExpanded ? warnings : warnings.slice(0, 2)).map((warn, idx) => (
              <li key={idx} style={{ marginBottom: 4 }}>
                <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
                  {warn.message || JSON.stringify(warn)}
                </Typography>
              </li>
            ))}
          </Box>
          {warnings.length > 2 && (
            <Box sx={{ mt: 1, pl: 2 }}>
              <Button
                variant="text"
                size="small"
                onClick={() => setIsWarningsExpanded(!isWarningsExpanded)}
                endIcon={isWarningsExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                sx={{
                  p: 0,
                  minWidth: 'auto',
                  fontWeight: 700,
                  fontSize: '0.8125rem',
                  textTransform: 'none',
                  color: theme.palette.warning?.dark || '#c75100',
                  '&:hover': {
                    backgroundColor: 'transparent',
                    textDecoration: 'underline',
                  },
                }}
              >
                {isWarningsExpanded ? 'Read Less' : `Read More (+${warnings.length - 2} more)`}
              </Button>
            </Box>
          )}
        </Alert>
      )}
    </Box>
  );
};

export default RateSheetPreview;
